
import { defineComponent, ref, computed, reactive, watch, PropType } from 'vue'
import StockRangeFactorItem from './StockRangeFactorItem.vue'
import BoardSelectorPopup from './BoardSelectorPopup.vue'
import FiLabelSub from './FiLabelSub.vue'
import starApi from '@/api/starApi'
import { resolveFactorSubLabelText } from '@/utils/starFactorLabel'

const BOARD_KEYWORDS = ['行业', '概念', '题材', '地区']

export default defineComponent({
  name: 'StockRangeSelector',
  components: { StockRangeFactorItem, BoardSelectorPopup, FiLabelSub },
  props: {
    rangeData: { type: Array as PropType<any[]>, default: () => [] },
    initialSelection: { type: Array as PropType<any[]>, default: () => [] },
    isLight: Boolean,
    colors: { type: Object as PropType<Record<string, string>>, required: true },
    headers: { type: Object as PropType<Record<string, string>>, default: () => ({}) }
  },
  emits: ['change'],
  setup(props, { emit }) {
    const selected = ref<any[]>([])
    const selectedCodes = computed(() => new Set(selected.value.map((s: any) => s.attrCode)))

    const boardSelected = reactive<Record<string, any[]>>({})

    const boardPopup = reactive({
      itemCode: '',
      options: [] as any[],
      loading: false
    })

    const rangeTree = computed(() => props.rangeData || [])

    const isBoardItem = (item: any) =>
      BOARD_KEYWORDS.some(kw => item.attrName?.includes(kw))

    const mergeSelectedIntoItem = (base: any) => {
      const found = selected.value.find((s: any) => s.attrCode === base.attrCode)
      if (!found) return base
      return {
        ...base,
        selectedOptions: found.selectedOptions || [],
        selectedRange: found.selectedRange,
        unionMode: found.unionMode,
        _confirmed: found._confirmed,
        _userModified: found._userModified,
        selectionDescript: found.selectionDescript,
      }
    }

    const normalizeItem = (item: any) => {
      const base = item.attrType == null ? { ...item, attrType: 1 } : item
      if ([3, 4, 5, 6, 7].includes(base.attrType)) {
        return mergeSelectedIntoItem(base)
      }
      return base
    }

    const tagStyle = (isSelected: boolean) => ({
      background: isSelected ? '#FFEDED' : (props.isLight ? '#f0f0f0' : '#2a2a2a'),
      color: isSelected ? '#1a1a1a' : props.colors.textPrimary,
      borderColor: 'transparent'
    })

    const openBoard = async (item: any) => {
      boardPopup.itemCode = item.attrCode
      boardPopup.options = []
      boardPopup.loading = true
      try {
        const res = await starApi.getStockFactorOptions({
          data: { factorCode: item.attrCode },
          headers: props.headers
        })
        const raw: any[] = res?.data || []
        boardPopup.options = raw.map((o: any) => ({
          attrCode: o.plateCode ?? o.optionCode ?? o.attrCode,
          attrName: o.plateName ?? o.optionName ?? o.attrName,
          changeRate: o.pxChangeRate ?? o.changeRate,
          market: o.market,
          firstLetter: o.firstLetter ?? ''
        }))
      } catch (e) { /* 静默 */ }
      finally { boardPopup.loading = false }
    }

    const onBoardConfirm = (itemCode: string, opts: any[]) => {
      boardSelected[itemCode] = opts
      boardPopup.itemCode = ''
      emitChange()
    }

    const clearBoard = (code: string) => {
      boardSelected[code] = []
      emitChange()
    }

    const isItemSelected = (item: any) => {
      const found = selected.value.find((s: any) => s.attrCode === item.attrCode)
      if (item.attrType === 7) {
        return selectedCodes.value.has(item.attrCode)
      }
      if ([3, 4, 5, 6].includes(item.attrType)) {
        return !!(found?._confirmed || found?.selectedOptions?.length)
      }
      return selectedCodes.value.has(item.attrCode)
    }

    const formatHintText = (found: any) => {
      const text = resolveFactorSubLabelText(found)
      if (!text) return ''
      return text.replace(/ 或 /g, '、').replace(/ 且 /g, '、')
    }

    const getSelectedHint = (item: any) => {
      const found = selected.value.find((s: any) => s.attrCode === item.attrCode)
      if (!found) return ''
      if (item.attrType === 7) {
        const n = found.selectedOptions?.length || 0
        return n > 0 ? `区间内共${n}只` : ''
      }
      if ([3, 4, 5, 6].includes(item.attrType)) {
        return formatHintText(found)
      }
      return ''
    }

    const onToggle = (item: any) => {
      /** 卡片右上角 ×：从已选列表中移除，避免与「区间已确认 / 多选」的更新逻辑混淆 */
      if (item._tagRemove) {
        const i = selected.value.findIndex((s: any) => s.attrCode === item.attrCode)
        if (i >= 0) selected.value.splice(i, 1)
        emitChange()
        return
      }
      const idx = selected.value.findIndex((s: any) => s.attrCode === item.attrCode)
      if (item.attrType === 7 || item._confirmed) {
        if (idx >= 0) selected.value.splice(idx, 1, item)
        else selected.value.unshift(item)
      } else if ([3, 4, 5, 6].includes(item.attrType)) {
        if (item._confirmed && item.selectedOptions?.length) {
          if (idx >= 0) selected.value.splice(idx, 1, item)
          else selected.value.unshift(item)
        } else if (item.selectedOptions?.length) {
          if (idx >= 0) selected.value.splice(idx, 1, item)
          else selected.value.unshift(item)
        } else if (idx >= 0) {
          selected.value.splice(idx, 1)
        }
      } else {
        if (idx >= 0) selected.value.splice(idx, 1)
        else selected.value.unshift(item)
      }
      emitChange()
    }

    const emitChange = () => {
      const boardItems = Object.entries(boardSelected)
        .filter(([, opts]) => opts.length > 0)
        .map(([itemCode, opts]) => {
          const parentItem = rangeTree.value
            .flatMap((g: any) => g.childAttrList || [])
            .find((item: any) => item.attrCode === itemCode)
          return {
            attrCode: itemCode,
            attrName: parentItem?.attrName || itemCode,
            attrType: 5,
            selectedOptions: opts,
            _isBoard: true
          }
        })
      emit('change', selected.value.concat(boardItems))
    }

    const applyInitialSelection = (val: any[]) => {
      selected.value = []
      Object.keys(boardSelected).forEach((k) => { boardSelected[k] = [] })
      for (const item of val) {
        if (item._isBoard) {
          boardSelected[item.attrCode] = [...(item.selectedOptions || [])]
        } else {
          selected.value.push({ ...item })
        }
      }
      emitChange()
    }

    const buildInitialKey = (val: any[]) =>
      val.map((f: any) => {
        const opts = (f.selectedOptions || []).map((o: any) => o.attrCode).join(',')
        const mode = f.unionMode || ''
        const confirmed = f._confirmed ? '1' : '0'
        return `${f.attrCode}:${opts}:${mode}:${confirmed}`
      }).join('|')

    let appliedInitialKey = ''
    const tryApplyInitialSelection = (val: any[]) => {
      if (!val?.length || !(props.rangeData || []).length) return
      const key = buildInitialKey(val)
      if (key === appliedInitialKey) return
      appliedInitialKey = key
      applyInitialSelection(val)
    }

    watch(() => props.initialSelection, (val) => {
      tryApplyInitialSelection(val || [])
    }, { immediate: true, deep: true })

    watch(
      () => props.rangeData,
      () => {
        if (props.initialSelection?.length) {
          appliedInitialKey = ''
          tryApplyInitialSelection(props.initialSelection)
        }
      },
    )

    return {
      rangeTree, selected, selectedCodes, boardSelected, boardPopup,
      isBoardItem, normalizeItem, tagStyle,
      openBoard, onBoardConfirm, clearBoard,
      isItemSelected, getSelectedHint, onToggle
    }
  }
})
